/**
 * city/vegetation/trees-other.js
 * Các loài thực vật & chậu cây cảnh khác: cây đa, khóm tre, chuối, chậu quất/hoa giấy/cau cảnh
 */

import * as THREE from 'three';
import { createRng } from '../builder-core.js';

class MethodsHolder {
  // CÂY ĐA: gốc to có rễ bạnh, tán rất rộng, rễ phụ buông rủ xuống như rèm
  createBanyanTree(options = {}) {
    const tree = new THREE.Group();
    tree.name = 'Tree_CayDa_Voxel';
    const rnd = createRng(options.seed ?? 77);
    const bark = this.mat('#6b5a4a', { roughness: 0.95 });
    const top = this._trunk(tree, { height: 2.6, baseW: 0.95, topW: 0.6, segments: 3, color: '#6b5a4a' });
    // Rễ bạnh quanh gốc
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      this._limb(
        tree,
        new THREE.Vector3(Math.cos(a) * 0.35, 0.7, Math.sin(a) * 0.35),
        new THREE.Vector3(Math.cos(a) * 1.0, 0.02, Math.sin(a) * 1.0),
        0.2,
        bark
      );
    }
    // Cành lớn toả ngang
    const limbEnds = [];
    for (let i = 0; i < 5; i++) {
      const a = (i / 5) * Math.PI * 2 + 0.3;
      const end = new THREE.Vector3(Math.cos(a) * 2.2, top.y + 0.7, Math.sin(a) * 2.0);
      this._limb(tree, top, end, 0.24, bark);
      limbEnds.push(end);
    }
    this._canopy(
      tree,
      {
        x: 0,
        y: top.y + 1.4,
        z: 0,
        rx: 3.2,
        ry: 1.1,
        rz: 3.0,
        palette: ['#14532d', '#166534', '#15803d', '#365314'],
        count: 120
      },
      rnd
    );
    // Rễ phụ buông rủ
    const rootMat = this.mat('#8a7560', { roughness: 0.95 });
    for (let i = 0; i < 16; i++) {
      const a = rnd() * Math.PI * 2;
      const r = 0.9 + rnd() * 1.9;
      const len = 1.2 + rnd() * 1.6;
      this.box(tree, 0.05, len, 0.05, rootMat, Math.cos(a) * r, top.y + 0.6 - len / 2, Math.sin(a) * r, null, false);
    }
    return tree;
  }

  // KHÓM TRE: gò đất, 12 cây tre vươn cao toả ra ngoài có đốt, lá mảnh ở ngọn
  createBambooClump(options = {}) {
    const tree = new THREE.Group();
    tree.name = 'Tree_KhomTre_Voxel';
    const rnd = createRng(options.seed ?? 91);
    this.box(tree, 1.3, 0.2, 1.1, this.mat('#5b3a1e', { roughness: 1 }), 0, 0.1, 0);
    const culmColors = ['#65a30d', '#4d7c0f', '#84cc16'];
    const nodeMat = this.mat('#3f6212', { roughness: 0.8 });
    const leafColors = ['#4d7c0f', '#65a30d', '#84cc16', '#3f6212'];
    for (let i = 0; i < 12; i++) {
      const a = rnd() * Math.PI * 2;
      const r = rnd() * 0.4;
      const base = new THREE.Vector3(Math.cos(a) * r, 0.15, Math.sin(a) * r);
      const lean = 0.08 + rnd() * 0.2;
      const height = 4.2 + rnd() * 2.2;
      const dir = new THREE.Vector3(Math.cos(a) * lean, 1, Math.sin(a) * lean).normalize();
      const tip = base.clone().addScaledVector(dir, height);
      this._limb(tree, base, tip, 0.08, this.mat(culmColors[i % culmColors.length], { roughness: 0.6 }));
      // Đốt tre
      for (let n = 0.6; n < height; n += 0.62) {
        const p = base.clone().addScaledVector(dir, n);
        this.box(tree, 0.1, 0.03, 0.1, nodeMat, p.x, p.y, p.z, null, false);
      }
      // Cụm lá mảnh ở 1/3 trên
      for (let k = 0; k < 4; k++) {
        const p = base.clone().addScaledVector(dir, height * (0.62 + k * 0.1));
        const side = rnd() * Math.PI * 2;
        this.box(
          tree,
          0.42,
          0.05,
          0.14,
          this.mat(leafColors[(i + k) % leafColors.length], { roughness: 0.8 }),
          p.x + Math.cos(side) * 0.22,
          p.y,
          p.z + Math.sin(side) * 0.22,
          [0, -side, -0.35]
        );
      }
    }
    return tree;
  }

  // CÂY CHUỐI: thân giả xanh, tàu lá to bản vươn lên rồi rủ, lá khô rủ sát thân, buồng chuối & bắp chuối tím
  createBananaTree(options = {}) {
    const tree = new THREE.Group();
    tree.name = 'Tree_Chuoi_Voxel';
    const rnd = createRng(options.seed ?? 105);
    this.box(tree, 0.9, 0.12, 0.9, this.mat('#5b3a1e', { roughness: 1 }), 0, 0.06, 0);
    this.box(tree, 0.28, 1.8, 0.28, this.mat('#65a30d', { roughness: 0.8 }), 0, 1.0, 0);
    this.box(tree, 0.3, 0.9, 0.3, this.mat('#4d7c0f', { roughness: 0.85 }), 0, 0.55, 0);
    const topY = 1.9;
    const leafColors = ['#4ade80', '#22c55e', '#16a34a'];
    for (let i = 0; i < 7; i++) {
      const a = (i / 7) * Math.PI * 2 + rnd() * 0.3;
      const col = this.mat(leafColors[i % leafColors.length], { roughness: 0.75 });
      const dir = new THREE.Vector3(Math.cos(a), 0, Math.sin(a));
      // Đoạn trong vươn lên, đoạn ngoài rủ xuống
      const inner = this.box(tree, 0.7, 0.03, 0.4, col, dir.x * 0.4, topY + 0.3, dir.z * 0.4);
      inner.rotation.set(0, -a, 0.45);
      const outer = this.box(tree, 0.7, 0.03, 0.44, col, dir.x * 0.98, topY + 0.38, dir.z * 0.98);
      outer.rotation.set(0, -a, -0.35);
    }
    // Lá khô rủ sát thân
    const dry = this.mat('#a16207', { roughness: 0.9 });
    [0.8, 2.6].forEach((a) => {
      const leaf = this.box(tree, 0.05, 0.9, 0.3, dry, Math.cos(a) * 0.2, 1.3, Math.sin(a) * 0.2);
      leaf.rotation.y = -a;
    });
    // Buồng chuối & bắp chuối
    const fruit = this.mat('#a3e635', { roughness: 0.7 });
    for (let r = 0; r < 3; r++) {
      this.box(tree, 0.3, 0.1, 0.3, fruit, 0.28, 1.6 - r * 0.14, 0.05);
    }
    this.box(tree, 0.14, 0.2, 0.14, this.mat('#7e22ce', { roughness: 0.7 }), 0.3, 1.13, 0.05);
    return tree;
  }

  // CHẬU CÂY CẢNH trước cửa nhà: 'quat' (quất Tết), 'hoa_giay' (hoa giấy), 'cau' (cau cảnh)
  createPottedPlant(kind = 'quat', options = {}) {
    const plant = new THREE.Group();
    plant.name = `Pot_${kind}`;
    const rnd = createRng(options.seed ?? kind.length * 13);
    const pot = this.mat(kind === 'cau' ? '#e7e5e4' : '#b45309', { roughness: 0.85 });
    this.box(plant, 0.46, 0.34, 0.46, pot, 0, 0.17, 0);
    this.box(plant, 0.54, 0.07, 0.54, pot, 0, 0.36, 0);
    this.box(plant, 0.4, 0.04, 0.4, this.mat('#5b3a1e', { roughness: 1 }), 0, 0.38, 0, null, false);
    if (kind === 'cau') {
      const top = new THREE.Vector3(0.05, 1.5, 0);
      this._limb(plant, new THREE.Vector3(0, 0.4, 0), top, 0.08, this.mat('#a3a380', { roughness: 0.8 }));
      for (let f = 0; f < 6; f++) {
        const a = (f / 6) * Math.PI * 2;
        const leaf = this.box(
          plant,
          0.6,
          0.03,
          0.2,
          this.mat(f % 2 ? '#16a34a' : '#22c55e', { roughness: 0.8 }),
          top.x + Math.cos(a) * 0.3,
          top.y - 0.05,
          top.z + Math.sin(a) * 0.3
        );
        leaf.rotation.set(0, -a, -0.35);
      }
      return plant;
    }
    const isQuat = kind === 'quat';
    this._limb(plant, new THREE.Vector3(0, 0.4, 0), new THREE.Vector3(0, 0.7, 0), 0.07, this.mat('#5b4636'));
    this._canopy(
      plant,
      {
        x: 0,
        y: 0.95,
        z: 0,
        rx: 0.42,
        ry: 0.4,
        rz: 0.42,
        palette: ['#166534', '#15803d', '#16a34a'],
        count: 26,
        accent: isQuat ? ['#f97316', '#fb923c', '#fbbf24'] : ['#db2777', '#ec4899', '#be185d'],
        accentRatio: isQuat ? 0.4 : 0.55
      },
      rnd
    );
    return plant;
  }
}

export function registerTreesOtherMethods(proto) {
  Object.getOwnPropertyNames(MethodsHolder.prototype).forEach((name) => {
    if (name !== 'constructor') {
      proto[name] = MethodsHolder.prototype[name];
    }
  });
}
